import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import CommunityPulse from "@/components/CommunityPulse";
import UpcomingEvents from "@/components/UpcomingEvents";
import EditableText from "@/components/EditableText";
import PixoHero from "@/components/pixo/PixoHero";
import { useEffect, useState } from "react";
import HomeSearch from "@/components/home/HomeSearch";

const BASE_BTN = "inline-flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-6 py-2.5 sm:py-3 min-h-[44px] text-[11px] sm:text-xs uppercase tracking-[0.12em] sm:tracking-[0.2em] font-bold rounded-sm whitespace-nowrap";
const MAIN = `btn-gold ${BASE_BTN}`;
const SECOND = `btn-ghost-gold ${BASE_BTN}`;
import StoreLevel from "@/components/home/StoreLevel";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";

// patch 34: "Votar agora" pulses while a signed-in member still has rewarded votes today
function useVotesLeft() {
  const { user } = useAuth();
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!user || user.is_admin) { setLeft(0); return undefined; }
    let alive = true;
    api.get("/users/me/xp").then(({ data }) => alive && setLeft(data.votes_left || 0)).catch(() => {});
    return () => { alive = false; };
  }, [user?.id, user?.is_admin]); // eslint-disable-line react-hooks/exhaustive-deps
  return left;
}

// integer pixel scale for the hero Pixo per breakpoint (keeps pixel art crisp)
function useHeroScale() {
  const pick = () => {
    if (typeof window === "undefined") return 5;
    const w = window.innerWidth;
    return w < 640 ? 3 : w < 1024 ? 4 : w < 1280 ? 5 : 6;
  };
  const [s, setS] = useState(pick);
  useEffect(() => {
    const on = () => setS(pick());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return s;
}

export default function Home() {
  const { t, lang } = useI18n();
  const heroScale = useHeroScale();
  const votesLeft = useVotesLeft();
  const { user } = useAuth();
  // patch 35: the hero Pixie loads after the title and buttons are on screen
  const [heroReady, setHeroReady] = useState(false);
  useEffect(() => {
    const idle = window.requestIdleCallback || ((f) => setTimeout(f, 400));
    const id = idle(() => setHeroReady(true));
    return () => (window.cancelIdleCallback ? window.cancelIdleCallback(id) : clearTimeout(id));
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        path="/"
        title={lang === "en" ? "Portugal's TCG rankings: stores, events and creators" : "Rankings TCG de Portugal: lojas, eventos e criadores"}
        description={lang === "en" ? "The best TCG stores, card show events and content creators in Portugal (Pokémon, Magic, One Piece, Yu-Gi-Oh!), rated by tierTCG and voted by the community." : "As melhores lojas de TCG, eventos e criadores de conteúdo em Portugal (Pokémon, Magic, One Piece, Yu-Gi-Oh!), avaliados pelo tierTCG e votados pela comunidade."}
        jsonLd={[
          { "@type": "Organization", name: "tierTCG", url: "https://tiertcg.com/", logo: "https://tiertcg.com/apple-touch-icon.png", sameAs: ["https://www.instagram.com/tiertcg"] },
          { "@type": "WebSite", name: "tierTCG", url: "https://tiertcg.com/", inLanguage: "pt-PT" },
        ]}
      />
      <section
        className="hero-tcg-texture hero-bg pt-24 pb-6 sm:pt-28 sm:pb-12 lg:pt-36 lg:pb-16"
        style={{
          "--hero-sm": `url(${process.env.PUBLIC_URL}/brand/hero-bg-960.webp)`,
          "--hero-lg": `url(${process.env.PUBLIC_URL}/brand/hero-bg.webp)`,
          backgroundSize: "cover",
          backgroundPosition: "right center",
          backgroundRepeat: "no-repeat",
          backgroundColor: "#0A0A0C",
        }}
      >
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 relative">
          {heroScale > 3 && heroReady && (
            <div className="absolute right-5 top-0 lg:right-[6%] xl:right-[10%] lg:top-6">
              <PixoHero scale={heroScale} />
            </div>
          )}
          <div className="flex flex-col lg:flex-row lg:items-end gap-10">
            <div className="flex-1">
              <h1 className="font-display text-[40px] sm:text-6xl lg:text-8xl font-black uppercase tracking-tight leading-[0.92] text-stone-100 text-balance">
                <EditableText k="home.title_1">{t.home.title_1}</EditableText><br />
                <EditableText k="home.title_2" className="text-amber-400">{t.home.title_2}</EditableText>{" "}
                <EditableText k="home.title_3">{t.home.title_3}</EditableText><br />
                <EditableText k="home.title_4">{t.home.title_4}</EditableText>{" "}
                <EditableText k="home.title_5" className="text-stone-300">{t.home.title_5}</EditableText>
              </h1>
              {/* patch 27: on phones Pixie lives in the bottom bar */}
              <p className="text-[15px] sm:text-lg text-stone-400 leading-relaxed mt-4 sm:mt-6 max-w-xl">
                <EditableText k="home.lede" multiline>{t.home.lede}</EditableText>
              </p>
              <HomeSearch />
              {/* patch 35: one main action per visitor — members vote, visitors find a store near them */}
              <div className="grid grid-cols-2 gap-2 mt-3 sm:flex sm:items-center sm:gap-3 sm:mt-8">
                {/* patch 45: the same "Vote now" for everyone (signed out too) on every device */}
                <Link to="/vote" data-testid="hero-cta-vote" data-pulse={user && votesLeft > 0 ? "on" : undefined} className={`${MAIN} vote-cta`}>
                  <EditableText k="home.cta_vote">{t.home.cta_vote}</EditableText> <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/explore" data-testid="hero-cta-explore" className={SECOND}>
                  <EditableText k="home.cta_explore">{t.home.cta_explore}</EditableText>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <CommunityPulse />
      <StoreLevel />
      <UpcomingEvents />
    </div>
  );
}
